import {CameraOutlined, QuestionCircleOutlined, SettingOutlined} from '@ant-design/icons'
import {
  Alert,
  Button as AntdButton,
  Button,
  Col,
  Divider,
  Dropdown,
  MenuProps,
  message,
  Popconfirm,
  Row,
  Tag,
  theme,
  Tour,
  TourProps,
} from 'antd'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Link, useNavigate, useRevalidator, useRouteLoaderData} from 'react-router-dom'
import {Project} from '../../entities/project'
import {CalendarReminders} from '../calendarRemiders/CalendarReminders'
import {CanvasViewEditor} from '../canvas/CanvasViewEditor'
import {Reminders} from '../reminders/Reminders'
import {Plant} from '../../entities/plant'
import {ToDo} from '../../entities/todoList'
import {PlantsTable} from '../plantsTable/PlantsTable'
import {instance as projectsService} from '../../services/ProjectsService'
import {instance as plantsService} from '../../services/PlantsService'
import {instance as eventsService} from '../../services/PlantEventsJournalService'
import {useIntl} from 'react-intl'
import {parseFilters, PlantedPlantsFilters} from '../../services/PlantsService'
import {orange} from '@ant-design/colors'
import {PlantModal} from '../plantForm/PlantModal'
import {ConfirmWithCheckModal} from '../plantForm/components/ConfirmWithCheckModal'
import './ViewProject.less'
import {GardenPlanVariant} from '../../entities/gardenPlanVariant'
import {GardenPlanVariantSelector} from './GardenPlanVariantSelector'
import {markProjectForRevalidation} from '../../routes/projectRevalidator'
import {useGardenPlanVariantParam} from '../../hooks/useGardenPlanVariantParam'

export function ViewProject({isSnapshot = false}: {isSnapshot?: boolean}) {
  const revalidator = useRevalidator()
  const [messageApi, contextHolder] = message.useMessage()

  const {
    token: {controlHeightSM},
  } = theme.useToken()

  const {
    project,
    projectPlants,
    projectToDos,
    plantedPlantsNamesWithId,
    projectGardenPlanVariants,
  } = useRouteLoaderData(isSnapshot ? 'snapshot' : 'project') as {
    project: Project
    projectPlants: Plant[]
    projectToDos: ToDo[]
    plantedPlantsNamesWithId: {id: number; name: string}[]
    projectGardenPlanVariants: GardenPlanVariant[]
  }

  const {gardenPlanVariantId, setGardenPlanVariantId, withGardenPlanVariant} =
    useGardenPlanVariantParam()
  // drop ids from the URL that don't belong to this project (e.g. a deleted variant)
  const activeGardenPlanVariantId = projectGardenPlanVariants.some(
    (variant) => variant.id === gardenPlanVariantId
  )
    ? gardenPlanVariantId
    : null

  useEffect(() => {
    if (gardenPlanVariantId && !activeGardenPlanVariantId) {
      setGardenPlanVariantId(null)
    }
  }, [gardenPlanVariantId, activeGardenPlanVariantId, setGardenPlanVariantId])

  const currentProjectPlants = useMemo(
    () =>
      projectPlants.filter(
        (plant) =>
          !plant.gardenPlanVariantId || plant.gardenPlanVariantId === activeGardenPlanVariantId
      ),
    [projectPlants, activeGardenPlanVariantId]
  )

  // const [filters, setFilters] = useState<PlantedPlantsFilters>({})

  // const filteredPlants = projectPlants.filter((plant) => {
  //   if (filters.date) {
  //     if (
  //       new Date(plant.dateOfPlantingUtc) < filters.date.startDate ||
  //       new Date(plant.dateOfPlantingUtc) > filters.date.endDate
  //     ) {
  //       return false
  //     }
  //   }
  //   if (filters.plant) {
  //     if (plant.name.toLowerCase().indexOf(filters.plant.toLowerCase()) < 0) {
  //       return false
  //     }
  //   }
  //   if (filters.projects) {
  //     if (filters.projects.indexOf(String(plant.projectId)) < 0) {
  //       return false
  //     }
  //   }

  //   return true
  // })

  const intl = useIntl()
  const navigate = useNavigate()

  const onDeleteConfirm = async (isDeletePlants: boolean, isDeletePlantsEvents: boolean) => {
    const result = await projectsService.deleteProject(
      project.id,
      isDeletePlants,
      isDeletePlantsEvents
    )
    if (result.success === true) {
      navigate('/projects')
      revalidator.revalidate()
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false)

  const itemsProjectSettings: MenuProps['items'] = [
    {
      key: 'delete',
      label: (
        <Button type="text" onClick={() => setOpenDeleteModal(true)}>
          {intl.formatMessage({id: 'deleteProject'})}
        </Button>

        // <Popconfirm
        //   placement="bottom"
        //   title={intl.formatMessage({id: 'areYouSureYouWantToDelete'})}
        //   description={intl.formatMessage({id: 'thisOperationIsIrreversible'})}
        //   icon={<QuestionCircleOutlined style={{color: 'red'}} />}
        //   onConfirm={async () => {
        //     const result = await projectsService.deleteProject(project.id)
        //     if (result.success === true) {
        //       navigate('/projects')
        //       revalidator.revalidate()
        //     } else {
        //       messageApi.open({
        //         type: 'error',
        //         content: result.error?.message,
        //       })
        //     }
        //   }}
        //   okText={intl.formatMessage({id: 'yes'})}
        //   cancelText={intl.formatMessage({id: 'no'})}
        // >
        //   {intl.formatMessage({id: 'deleteProject'})}
        // </Popconfirm>
      ),
    },
  ]

  const refEdit = useRef<any>(null)
  const refAddPlant = useRef<any>(null)
  const refSketch = useRef<HTMLDivElement>(null)
  const refSnapshot = useRef<HTMLDivElement>(null)
  const refReminders = useRef<HTMLDivElement>(null)

  const openPlant = (plantId: number) =>
    navigate(withGardenPlanVariant(`/projects/${project.id}/plants/${plantId}`))

  const [openTour, setOpenTour] = useState<boolean>(false)

  const steps: TourProps['steps'] = [
    {
      title: intl.formatMessage({id: 'projectPage'}),
      description: intl.formatMessage({id: 'projectPageTourDescriptionAll'}),
      target: null,
    },
    {
      title: intl.formatMessage({id: 'editProject'}),
      description: intl.formatMessage({id: 'projectPageTourDescriptionEdit'}),
      target: () => refEdit.current!,
    },
    {
      title: intl.formatMessage({id: 'addPlant'}),
      description: intl.formatMessage({id: 'projectPageTourDescriptionAddPlant'}),
      target: () => refAddPlant.current!,
    },
    {
      title: intl.formatMessage({id: 'plantPlant'}),
      description: intl.formatMessage({id: 'projectPageTourDescriptionCanvas'}),
      target: null,
    },
    // {
    //   title: intl.formatMessage({id: 'projectSnapshot'}),
    //   description: intl.formatMessage({id: 'projectPageTourDescriptionSnapshot'}),
    //   target: () => refSnapshot.current!,
    // },
    {
      title: intl.formatMessage({id: 'projectReminders'}),
      description: intl.formatMessage({id: 'projectPageTourDescriptionReminders'}),
      placement: 'left',
      target: null, //() => refReminders.current!,
    },
  ]

  return (
    <div>
      <div>
        <div className="project-title">
          <h2 style={{flex: '1 1 auto'}}>{project.name}</h2>
          <div className="project-title__buttons">
            <Button onClick={() => setOpenTour(true)}>
              {intl.formatMessage({id: 'projectPageTour'})}
            </Button>
            {/* {false &&
                !isSnapshot && ( //snapshots are disabled
                  <div ref={refSnapshot}>
                    <Popconfirm
                      placement="bottom"
                      title={intl.formatMessage({id: 'areYouSureYouWantToMakeSnapshot'})}
                      description={intl.formatMessage({id: 'makingSnapshotDescription'})}
                      icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                      onConfirm={async () => {
                        const result = await projectsService.makeProjectSnapshot(project.id)
                        if (result.success === true) {
                          navigate('/snapshots')
                          revalidator.revalidate()
                        } else {
                          messageApi.open({
                            type: 'error',
                            content: result.error?.message,
                          })
                        }
                      }}
                      okText={intl.formatMessage({id: 'yes'})}
                      cancelText={intl.formatMessage({id: 'no'})}
                    >
                      <Button>{intl.formatMessage({id: 'makeSnapshot'})}</Button>
                    </Popconfirm>
                  </div>
                )} */}
            {!isSnapshot && (
              <Link
                type="link"
                style={{color: orange[5]}}
                to={withGardenPlanVariant(`/projects/${project.id}/edit`)}
                ref={refEdit}
              >
                {intl.formatMessage({id: 'edit'})}
              </Link>
            )}
            {isSnapshot && (
              <Tag icon={<CameraOutlined />} color={orange[5]}>
                {intl.formatMessage({id: 'snapshot'})}
              </Tag>
            )}
            <Dropdown menu={{items: itemsProjectSettings}} placement="bottomLeft">
              <AntdButton type="link">
                <SettingOutlined />
              </AntdButton>
            </Dropdown>
          </div>
        </div>

        <Divider style={{height: 'auto'}}></Divider>
        <div className="view-project">
          <CanvasViewEditor
            plotShape={project.plotShape}
            elements={project.elements}
            editable={true}
            projectPlants={currentProjectPlants}
            projectId={project.id}
            hasMap={Boolean(project?.location?.map)}
            openPlantById={openPlant}
            gardenPlanSelector={
              !isSnapshot && (
                <>
                  {activeGardenPlanVariantId && (
                    <Alert
                      className="planning-mode-warning"
                      type="warning"
                      showIcon
                      title={intl.formatMessage({id: 'planningModeWarning'})}
                    />
                  )}
                  <GardenPlanVariantSelector
                    projectId={project.id}
                    gardenPlanVariants={projectGardenPlanVariants}
                    activeGardenPlanVariantId={activeGardenPlanVariantId}
                    onActiveGardenPlanVariantIdChange={setGardenPlanVariantId}
                    onGardenPlanVariantsListUpdated={() => {
                      markProjectForRevalidation()
                      revalidator.revalidate()
                    }}
                  />
                </>
              )
            }
          ></CanvasViewEditor>

          {/* <Divider style={{height: 'auto'}}></Divider> */}

          {/* <div ref={refAddPlant}> 
            <div style={{margin: '15px 0'}}>
              <h2 style={{display: 'inline'}}>{intl.formatMessage({id: 'myPlants'})}</h2>
            </div>
            <PlantsTable
              plants={filteredPlants}
              isLoading={false}
              handleTableChange={(_pagination, filters) => setFilters(parseFilters(filters))}
              getPlantLink={(plant: Plant) => `/projects/${project.id}/plants/${plant.id}`}
              parentRoute={`/projects/${project.id}`}
              newPlantRoute={`/projects/${project.id}/plants/new`}
              pagination={undefined}
            />
          </div> */}

          {!isSnapshot && (
            <>
              <div className="view-project__calendar" style={{margin: `${controlHeightSM}px 0`}}>
                <CalendarReminders
                  todos={projectToDos}
                  plantedPlantsNamesWithId={plantedPlantsNamesWithId}
                />
              </div>
              <div className="view-project__reminders" ref={refReminders}>
                <Reminders
                  todos={projectToDos}
                  plantedPlantsNamesWithId={plantedPlantsNamesWithId}
                ></Reminders>
              </div>
            </>
          )}
        </div>
      </div>
      <Tour
        open={openTour}
        onClose={() => setOpenTour(false)}
        steps={steps}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
      />
      <PlantModal routeWhenClosed={withGardenPlanVariant(`/projects/${project.id}`)} />
      <ConfirmWithCheckModal
        checkboxLabel={intl.formatMessage({id: 'deleteProjectWithPlantsConfirm'})}
        label={intl.formatMessage({id: 'areYouSureYouWantToDelete'})}
        isOpen={isOpenDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        onOk={onDeleteConfirm}
        checkboxLabelSecondСondition={intl.formatMessage({
          id: 'deleteProjectWithPlantsAndEventsConfirm',
        })}
      />
    </div>
  )
}
